import { Link } from 'react-router-dom';
import { ColumnHelper } from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { maskDecimal } from '@givebutter/util';
import { faBuilding, faRectangleHistory } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getComputedContactProps } from 'assets/js/pages/contacts/transform';
import { ToggleTicketCheckInButton } from 'components/pages/tickets/ToggleTicketCheckInButton';
import {
    curriedTransformTableKeyToReadable,
    transformTicketNumber,
} from 'components/pages/tickets/transform';
import * as Tooltip from 'components/tooltip/Tooltip';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { isoDateFromServer } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { DateTime } from 'luxon';
import { PromoCode, Ticket } from 'types/domain';

const createTicketColumns = (columnHelper: ColumnHelper<Ticket>) => ({
    contactName: (options: { accountIdPath?: string } = {}) =>
        columnHelper.accessor(row => row.first_name, {
            id: 'contact_name',
            header: curriedTransformTableKeyToReadable,
            cell: info => {
                const ticket = info.row.original;

                if (!ticket) {
                    return <EmptyData />;
                }

                if (!ticket.contact) {
                    return (
                        <p>
                            {ticket.first_name} {ticket.last_name}
                        </p>
                    );
                }

                const contactProps = getComputedContactProps(ticket.contact);
                const linkUrl = options.accountIdPath
                    ? `${options.accountIdPath}/${contactProps.linkUrl}`
                    : contactProps.linkUrl;

                return (
                    <Link className={'text-black hover:underline'} to={linkUrl}>
                        <div className="flex gap-2">
                            <p>{contactProps.contactName}</p>
                            {contactProps.isCompany && (
                                <Badge size="sm" color="primary">
                                    <FontAwesomeIcon icon={faBuilding} /> Company
                                </Badge>
                            )}
                        </div>
                    </Link>
                );
            },
            meta: {
                ignoreLink: true,
            },
        }),

    createdAt: () =>
        columnHelper.accessor(row => row.created_at, {
            id: 'created_at',
            header: curriedTransformTableKeyToReadable,
            cell: ({ getValue }) =>
                isoDateFromServer(getValue()).toLocaleString(DateTime.DATE_FULL),
        }),

    type: () =>
        columnHelper.accessor(row => row.type, {
            id: 'type',
            header: curriedTransformTableKeyToReadable,
            enableSorting: false,
            cell: info => {
                const bundle = info.row.original.bundle;

                return (
                    <div className={'flex items-center gap-2'}>
                        {bundle && (
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <FontAwesomeIcon
                                        className="text-gray-400"
                                        icon={faRectangleHistory}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.ContentNoStyles inverse>
                                        <Tooltip.Arrow inverse />
                                        <p className="text-white">{bundle.title}</p>
                                        <p className="italic text-gray-400">
                                            {`B-${bundle.id.toString()}`}
                                        </p>
                                    </Tooltip.ContentNoStyles>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        )}
                        {info.row.original.title}
                        {info.getValue() !== 'physical' && <Badge>Digital</Badge>}
                    </div>
                );
            },
        }),

    number: () =>
        columnHelper.accessor(row => row.uid_suffix, {
            id: 'uid_suffix',
            header: curriedTransformTableKeyToReadable,
            cell: info => transformTicketNumber(info.getValue()),
        }),

    checkedInAt: () =>
        columnHelper.accessor(row => row.checked_in_at, {
            id: 'checked_in_at',
            header: curriedTransformTableKeyToReadable,
            enableSorting: false,
            cell: info => <ToggleTicketCheckInButton ticket={info.row.original} />,
            meta: {
                className: '!py-0 !pr-0 !w-[150px]',
                ignoreLink: true,
            },
        }),

    latestCheckin: () =>
        columnHelper.accessor(row => row.latest_checkin, {
            id: 'latest_checkin',
            header: curriedTransformTableKeyToReadable,
            enableSorting: false,
            cell: info => {
                const checkin = info.getValue();
                return (
                    <p className="whitespace-nowrap">
                        {checkin?.user ? (
                            `${checkin.user.first_name} ${checkin.user.last_name}`
                        ) : (
                            <EmptyData />
                        )}
                    </p>
                );
            },
        }),

    email: () =>
        columnHelper.accessor(row => row.email, {
            id: 'email',
            header: curriedTransformTableKeyToReadable,
            enableSorting: false,
            cell: info => {
                const ticket = info.row.original;
                const email = ticket.contact?.primary_email ?? info.getValue();
                return <p className="whitespace-nowrap">{email ?? <EmptyData />}</p>;
            },
        }),

    phone: () =>
        columnHelper.accessor(row => row.phone, {
            id: 'phone',
            header: curriedTransformTableKeyToReadable,
            enableSorting: false,
            cell: info => {
                const ticket = info.row.original;
                const phone = ticket.contact?.phone ?? info.getValue();

                return (
                    <p className="whitespace-nowrap">
                        {phone ? getFormattedPhoneNumber(phone) : <EmptyData />}
                    </p>
                );
            },
        }),

    price: () =>
        columnHelper.accessor(row => row.price, {
            id: 'price',
            header: curriedTransformTableKeyToReadable,
            enableSorting: false,
            cell: info => {
                const price = info.getValue();
                const formatted = maskDecimal(price);

                return (
                    <p>
                        {price ? (
                            <>
                                <CurrencySymbol />
                                {formatted}
                            </>
                        ) : (
                            <EmptyData />
                        )}
                    </p>
                );
            },
        }),

    campaign: () =>
        columnHelper.accessor(row => row.campaign, {
            id: 'campaign',
            header: curriedTransformTableKeyToReadable,
            enableSorting: false,
            cell: info => {
                const campaign = info.getValue();

                if (campaign) {
                    return (
                        <div className={'flex max-w-[200px]'}>
                            <TruncatedText>{campaign.title}</TruncatedText>
                        </div>
                    );
                }

                return <EmptyData />;
            },
        }),

    discount: () =>
        columnHelper.accessor(row => row.discount as PromoCode | null, {
            id: 'discount',
            header: curriedTransformTableKeyToReadable,
            enableSorting: false,
            cell: info => {
                const discount = info.getValue();

                if (discount) {
                    return (
                        <div className={'flex max-w-[200px]'}>
                            <TruncatedText>{discount.code}</TruncatedText>
                        </div>
                    );
                }

                return <EmptyData />;
            },
        }),
});

export { createTicketColumns };
